import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VideoSection = () => {
  const [playing, setPlaying] = useState(false);
  const { t } = useTranslation('zimbra-admin-panel');
  const highlights = t('video.highlights', { returnObjects: true }) as string[];

  return (
    <section className="py-24 bg-gray-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-semibold mb-4 border border-blue-100 dark:border-blue-800">
            {t('video.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('video.title')}
          </h2>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t('video.description')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-slate-700 bg-black aspect-video">
              {!playing ? (
                <>
                  <img
                    src={`https://img.youtube.com/vi/Y-_4juoJNXE/maxresdefault.jpg`}
                    alt="Zimbra Admin Panel Demo Video"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/Y-_4juoJNXE/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40" />
                  <button
                    onClick={() => setPlaying(true)}
                    className="absolute inset-0 flex flex-col items-center justify-center group"
                    aria-label="Play demo video"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl group-hover:bg-blue-50 transition-colors"
                    >
                      <Play className="h-8 w-8 text-blue-600 ml-1" fill="currentColor" />
                    </motion.div>
                    <span className="mt-4 text-white font-semibold text-lg drop-shadow">
                      {t('video.watchDemo')}
                    </span>
                  </button>

                  <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded">
                    {t('video.fullDemo')}
                  </div>
                </>
              ) : (
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/Y-_4juoJNXE?autoplay=1&rel=0"
                  title="Zimbra Admin Panel Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>

            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl -z-10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('video.whatYouSee')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                {t('video.whatYouSeeDesc')}
              </p>
            </div>

            <ul className="space-y-4">
              {highlights.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>

            <div className="pt-4 border-t border-gray-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['#3B82F6', '#06B6D4', '#10B981'].map((color, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-white dark:border-slate-950"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('video.trusted')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
